import React from 'react'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import {
  DashboardMetricTileGrid,
  pickMetricsByIds,
} from '@components/pages/Dashboard/Shared/DashboardMetricTileGrid.component'
import type { UseInsuranceClaimsResult } from '@hooks/dashboard/useInsuranceClaims'
import {
  hasNonZeroDashboardValues,
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'

const CLAIMS_OUTSTANDING_IDS = [
  'ins-open-claims',
  'ins-submitted',
  'ins-outstanding-balance',
  'ins-avg-days-outstanding',
  'ins-pending-payer',
  'ins-appeals-open',
]

const PAYMENT_POSTING_IDS = [
  'ins-avg-days-to-pay',
  'ins-unposted-insurance',
  'ins-est-collections',
  'ins-avg-days-to-post',
  'ins-contractual-adj',
]

type InsuranceClaimsCardProps = {
  titleHref: string
  className?: string
  claims: Pick<
    UseInsuranceClaimsResult,
    'claimsOutstandingMetrics' | 'paymentPostingMetrics' | 'loading' | 'data'
  >
}

export function InsuranceClaimsCard({
  titleHref,
  className = 'dashboard-operations-scheduling-card',
  claims: { claimsOutstandingMetrics, paymentPostingMetrics, loading, data },
}: InsuranceClaimsCardProps): React.ReactElement {
  const claims = data?.claimsOutstanding
  const posting = data?.paymentPosting
  // appointmentPipeline is not rendered by this card (and is empty on the
  // overview fetch), so only the displayed KPI metrics decide emptiness.
  const hasSectionData = hasNonZeroDashboardValues(
    claims?.openClaims,
    claims?.submittedBalance,
    claims?.outstandingBalance,
    claims?.averageDaysOutstanding,
    claims?.pendingPayers,
    claims?.appealsOpen,
    posting?.averageDaysToPay,
    posting?.unpostedInsurance,
    posting?.estimatedInsuranceCollection,
    posting?.averageDaysToPost,
    posting?.contractualAdjustments,
  )
  const hasPayload = data != null
  const isEmpty = shouldShowDashboardSectionEmpty(loading, hasSectionData)
  const pending = isDashboardSectionPending(loading, hasPayload)

  return (
    <DashboardSectionCard
      title="Insurance claims"
      titleHref={titleHref}
      className={className}
      loading={pending}
      skeleton={{ metrics: 6, metricColumns: 3, block: 'default' }}
      isEmpty={isEmpty}
    >
      <div className="dashboard-operations-insurance-sections">
        <div className="dashboard-operations-insurance-section">
          <div className="dashboard-operations-insurance-section-label">
            Claims outstanding
          </div>
          <DashboardMetricTileGrid
            metrics={pickMetricsByIds(
              claimsOutstandingMetrics,
              CLAIMS_OUTSTANDING_IDS,
            )}
            columns={3}
          />
        </div>
        <div className="dashboard-operations-insurance-section">
          <div className="dashboard-operations-insurance-section-label">
            Payment & posting
          </div>
          <DashboardMetricTileGrid
            metrics={pickMetricsByIds(
              paymentPostingMetrics,
              PAYMENT_POSTING_IDS,
            )}
            columns={3}
          />
        </div>
      </div>
    </DashboardSectionCard>
  )
}
